import { ArchiveIcon, BanIcon, CircleCheckIcon, CircleIcon, CircleXIcon, CrosshairIcon, PauseIcon, PlayIcon, RocketIcon, SkipForwardIcon, SlidersHorizontalIcon, TimerIcon, TriangleAlertIcon } from "lucide-react";

// 版本状态文案按要求保留中文(全站 StateBadge 显示这里的 label)。
const state0 = {
  code: 0,
  color: "bg-chip-blue text-chip-blue-foreground",
  label: "规划中",
  icon: RocketIcon,
  nexts: [100, 280],
};
const state100 = {
  code: 100,
  color: "bg-chip-lime text-chip-lime-foreground",
  label: "预开发",
  icon: SkipForwardIcon,
  nexts: [0, 150],
};
const state150 = {
  code: 150,
  color: "bg-chip-cyan text-chip-cyan-foreground",
  label: "开发中",
  icon: PlayIcon,
  nexts: [100, 170],
};
const state170 = {
  code: 170,
  color: "bg-chip-teal text-chip-teal-foreground",
  label: "测试中",
  icon: SlidersHorizontalIcon,
  nexts: [150, 180],
};
const state180 = {
  code: 180,
  color: "bg-chip-green text-chip-green-foreground",
  label: "待发版",
  icon: PauseIcon,
  nexts: [170, 190, 200, 250, 260],
};

const state190 = {
  code: 190,
  color: "bg-chip-green text-chip-green-foreground animate-pulse",
  label: "审核中",
  icon: CrosshairIcon,
  nexts: [170, 200, 250, 260],
};
const state200 = {
  code: 200,
  color: "bg-chip-yellow text-chip-yellow-foreground",
  label: "放量中",
  icon: TimerIcon,
  nexts: [180, 250, 260, 271],
};
const state250 = {
  code: 250,
  color: "bg-chip-emerald text-chip-emerald-foreground",
  label: "线上",
  icon: CircleCheckIcon,
  nexts: [200, 270, 271],
};
const state260 = {
  code: 260,
  color: "bg-chip-red text-chip-red-foreground",
  label: "被拒",
  icon: CircleXIcon,
  // Rejected is recoverable: back to pre-release / in-review for a re-submit.
  nexts: [180, 190],
};
const state270 = {
  code: 270,
  color: "bg-muted text-muted-foreground",
  label: "已归档",
  icon: ArchiveIcon,
  nexts: [],
};
const state271 = {
  code: 271,
  color: "bg-chip-rose text-chip-rose-foreground",
  label: "下线",
  icon: TriangleAlertIcon,
  nexts: [],
};
// 废弃：规划中直接丢弃的版本(0 → 280),不曾进入开发,终态。
const state280 = {
  code: 280,
  color: "bg-muted text-muted-foreground",
  label: "废弃",
  icon: BanIcon,
  nexts: [],
};

/** 线上 (fully online): the delivery bar — close gates & "still active" checks. */
export const STATE_ONLINE = 250;
/** 规划中: the only state that allows back-to-pool / direct discard. */
export const STATE_PLANNING = 0;

export const States = {
  0: state0,
  100: state100,
  150: state150,
  170: state170,
  180: state180,
  190: state190,
  200: state200,
  250: state250,
  260: state260,
  270: state270,
  271: state271,
  280: state280,
};

export type VersionState = {
  code: number;
  color: string;
  label: string;
  icon: (typeof state0)["icon"];
  nexts: number[];
};

// ---- Runtime catalog ----
// The authoritative state catalog lives in the DB (`version_state` table,
// managed in the Admin panel; GET /version-states). The hardcoded States
// above are the seed/fallback: they pre-fill the registry so badges render
// before the catalog loads, and carry the icons/colors for built-in codes.
// `useVersionStates()` (hooks/version-states.ts) syncs the fetched rows in.

/** Chip tone key (stored in DB) → chip classes. Keep in sync with the
 * `--chip-*` tokens in globals.css and STATE_COLOR_TONES on the server. */
const TONE_CLASSES: Record<string, string> = {
  blue: "bg-chip-blue text-chip-blue-foreground",
  lime: "bg-chip-lime text-chip-lime-foreground",
  cyan: "bg-chip-cyan text-chip-cyan-foreground",
  teal: "bg-chip-teal text-chip-teal-foreground",
  green: "bg-chip-green text-chip-green-foreground",
  emerald: "bg-chip-emerald text-chip-emerald-foreground",
  yellow: "bg-chip-yellow text-chip-yellow-foreground",
  orange: "bg-chip-orange text-chip-orange-foreground",
  red: "bg-chip-red text-chip-red-foreground",
  rose: "bg-chip-rose text-chip-rose-foreground",
  purple: "bg-chip-purple text-chip-purple-foreground",
  muted: "bg-muted text-muted-foreground",
};
export const STATE_COLOR_TONES = Object.keys(TONE_CLASSES);

/** Chip classes for a tone key (admin previews); unknown/absent → muted. */
export const toneClass = (tone?: string | null): string =>
  (tone ? TONE_CLASSES[tone] : undefined) ?? TONE_CLASSES.muted;

export interface VersionStateDef {
  code: number;
  label: string;
  color?: string | null;
  nexts: number[];
}

const builtins = States as Partial<Record<number, VersionState>>;

// Display registry (label/color/icon per code). Seeded with the built-ins and
// overlaid by DB rows; entries for DB-deleted codes are kept so badges on
// historical data still show a label.
const registry = new Map<number, VersionState>(
  Object.values(States).map((s) => [s.code, s])
);
// Codes offered in config UIs (flow editor, notify states): the DB catalog
// once loaded, built-ins before that.
let catalogCodes: number[] = Object.values(States)
  .map((s) => s.code)
  .sort((a, b) => a - b);

/** Merge fetched catalog rows into the registry (called by useVersionStates). */
export const registerVersionStates = (rows: VersionStateDef[]) => {
  if (!rows.length) return;
  for (const r of rows) {
    const builtin = builtins[r.code];
    registry.set(r.code, {
      code: r.code,
      label: r.label,
      color:
        (r.color ? TONE_CLASSES[r.color] : undefined) ??
        builtin?.color ??
        TONE_CLASSES.muted,
      icon: builtin?.icon ?? CircleIcon,
      nexts: r.nexts ?? [],
    });
  }
  catalogCodes = rows.map((r) => r.code).sort((a, b) => a - b);
};

/** Display info for a code; misses only for codes never seen anywhere. */
export const stateInfo = (code: number | undefined): VersionState | undefined =>
  code === undefined ? undefined : registry.get(code);

/** Catalog codes in flow order (ascending — code value encodes the order). */
export const allStateCodes = (): number[] => [...catalogCodes];

/** Default flow graph: every catalog state with its default nexts, plus the
 * reserved `start` key — the entry state a newly created version lands in
 * (规划中 0 by default). */
export const defaultStateFlow = (): Record<string, number[]> => ({
  start: [STATE_PLANNING],
  ...Object.fromEntries(
    catalogCodes.map((c) => [String(c), registry.get(c)?.nexts ?? []])
  ),
});

/** The flow's entry state (reserved `start` key; legacy configs lack it). */
export const flowStart = (
  flow: Record<string, number[]> | null | undefined
): number => flow?.start?.[0] ?? STATE_PLANNING;

/** Per-app configurable flow (AppConfig.versionStateFlow): the app enables a
 * subset of the catalog codes and defines each state's nexts. No flow
 * configured → catalog default nexts; flow configured but the current state
 * is outside it (the flow was reconfigured after the version got there) →
 * offer every enabled node, so stranded versions can migrate onto the new
 * flow — once on a node, normal nexts resume. */
export const nextsFor = (
  flow: Record<string, number[]> | null | undefined,
  code: number | undefined
): number[] => {
  if (code === undefined) return [];
  if (!flow) return stateInfo(code)?.nexts ?? [];
  if (!(String(code) in flow)) {
    return Object.keys(flow)
      .filter((k) => k !== "start")
      .map(Number)
      .sort((a, b) => a - b);
  }
  return flow[String(code)] ?? [];
};
